import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BookOpenText,
  CalendarBlank,
  DownloadSimple,
  Eye,
  FileText,
  Folders,
  FunnelSimple,
  MagnifyingGlass,
  ShieldCheck,
} from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { API, publicUrl } from "../lib/api";

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return value;
  }
}

function TypeBadge({ type }) {
  const styles = {
    Documento: "bg-[#eef1ff] text-[#2346ff]",
    Procedimento: "bg-emerald-100 text-emerald-700",
    Formulario: "bg-amber-100 text-amber-700",
    Noticia: "bg-rose-100 text-rose-700",
  };

  return <Badge className={`text-xs ${styles[type] || "bg-slate-100 text-slate-600"}`}>{type}</Badge>;
}

export default function ManagerContentPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sector, setSector] = useState("Todos");
  const [type, setType] = useState("Todos");

  const loadItems = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/area-contents?scope=manager`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || "Nao foi possivel carregar conteudos da gerencia.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const sectors = useMemo(() => ["Todos", ...Array.from(new Set(items.map(item => item.department).filter(Boolean))).sort()], [items]);
  const types = useMemo(() => ["Todos", ...Array.from(new Set(items.map(item => item.content_type).filter(Boolean))).sort()], [items]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter(item => {
      const matchesQuery = !needle || [item.title, item.content, item.folder, item.department, item.created_by_name].join(" ").toLowerCase().includes(needle);
      const matchesSector = sector === "Todos" || item.department === sector;
      const matchesType = type === "Todos" || item.content_type === type;
      return matchesQuery && matchesSector && matchesType;
    });
  }, [items, query, sector, type]);

  const stats = useMemo(() => {
    return {
      total: items.length,
      procedures: items.filter(item => item.content_type === "Procedimento").length,
      forms: items.filter(item => item.content_type === "Formulario").length,
      sectors: sectors.length > 0 ? sectors.length - 1 : 0,
    };
  }, [items, sectors]);

  const openContent = (item) => {
    const target = item.source === "upload" ? item.file_url : item.url;
    if (target) window.open(target.startsWith("/api") ? publicUrl(target) : target, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#eef1ff] px-3 py-1.5 text-xs font-semibold text-[#2346ff]">
            <Eye className="h-4 w-4" weight="regular" />
            Visualizacao da Gerencia
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-950">Conteudos da Gerencia</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Acesse documentos, procedimentos e formularios publicados pelos autores com visibilidade para gerentes.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
            <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Pesquisar conteudo" className="h-11 w-full rounded-xl border-slate-200 bg-white pl-10 sm:w-72" />
          </div>
          <Button variant="outline" className="h-11 rounded-xl border-slate-200 text-slate-700" onClick={loadItems}>
            <FunnelSimple className="mr-2 h-4 w-4 text-[#2346ff]" weight="regular" />
            Atualizar
          </Button>
        </div>
      </div>

      {error && <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Documentos liberados", value: stats.total, icon: FileText },
          { label: "Procedimentos", value: stats.procedures, icon: BookOpenText },
          { label: "Formularios", value: stats.forms, icon: FileText },
          { label: "Setores origem", value: stats.sectors, icon: Folders },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="bento-card flex items-center gap-3 p-4">
            <div className="soft-icon h-11 w-11"><Icon className="h-5 w-5" weight="regular" /></div>
            <div>
              <p className="font-heading text-2xl font-bold text-slate-950">{value}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-4 grid gap-3 lg:grid-cols-[1fr_1fr_1.3fr]">
        <section className="bento-card p-4">
          <h2 className="mb-3 text-sm font-bold text-slate-900">Setor</h2>
          <div className="flex flex-wrap gap-2">
            {sectors.map(item => (
              <button key={item} type="button" onClick={() => setSector(item)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${sector === item ? "border-[#2346ff] bg-[#eef1ff] text-[#2346ff]" : "border-slate-200 bg-white text-slate-500"}`}>
                {item}
              </button>
            ))}
          </div>
        </section>

        <section className="bento-card p-4">
          <h2 className="mb-3 text-sm font-bold text-slate-900">Tipo</h2>
          <div className="flex flex-wrap gap-2">
            {types.map(item => (
              <button key={item} type="button" onClick={() => setType(item)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${type === item ? "border-[#2346ff] bg-[#eef1ff] text-[#2346ff]" : "border-slate-200 bg-white text-slate-500"}`}>
                {item}
              </button>
            ))}
          </div>
        </section>

        <section className="bento-card cute-surface p-4">
          <div className="flex gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#2346ff]" weight="regular" />
            <div>
              <h2 className="text-sm font-bold text-slate-900">Regra de acesso</h2>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Esta pagina mostra somente itens publicados e marcados para gerentes.
              </p>
            </div>
          </div>
        </section>
      </div>

      <section className="bento-card overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-heading text-base font-bold text-slate-900">Conteudos liberados</h2>
            <p className="mt-1 text-xs text-slate-500">Leitura e download conforme permissao do item.</p>
          </div>
          <Badge className="w-fit bg-[#eef1ff] text-[#2346ff]">Acesso de gerente</Badge>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Carregando conteudos...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <Folders className="mx-auto mb-3 h-8 w-8 text-[#2346ff]" weight="regular" />
            <p className="font-semibold text-slate-800">Nenhum conteudo liberado para gerentes.</p>
            <p className="mt-1 text-sm text-slate-500">Quando autores publicarem itens para gerentes, eles aparecerao aqui.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(item => (
              <article key={item.item_id} className="grid gap-4 p-4 transition hover:bg-[#f7f9ff] md:grid-cols-[1fr_auto] md:items-center">
                <div className="flex gap-3">
                  <span className="soft-icon h-11 w-11 flex-shrink-0"><FileText className="h-5 w-5" weight="regular" /></span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-900">{item.title}</h3>
                      <TypeBadge type={item.content_type} />
                    </div>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      {item.department || "Sem setor"} - {item.created_by_name || "Autor"} - {formatDate(item.updated_at)}
                    </p>
                    {item.content && <p className="mt-1 text-xs leading-5 text-slate-600">{item.content}</p>}
                  </div>
                </div>

                <div className="flex gap-2 md:justify-end">
                  <Button variant="outline" className="h-10 rounded-xl border-slate-200 text-slate-700" onClick={() => openContent(item)} disabled={!item.url && !item.file_url}>
                    <Eye className="mr-2 h-4 w-4 text-[#2346ff]" weight="regular" />
                    Abrir
                  </Button>
                  <Button variant="outline" className="h-10 rounded-xl border-slate-200 text-slate-700" onClick={() => openContent(item)} disabled={!item.url && !item.file_url}>
                    <DownloadSimple className="mr-2 h-4 w-4 text-[#2346ff]" weight="regular" />
                    Baixar
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
        <CalendarBlank className="h-4 w-4 text-[#2346ff]" weight="regular" />
        Atualizado conforme publicacoes dos autores.
      </div>
    </div>
  );
}
